import React, { useRef, useEffect, useState, forwardRef } from "react";

export interface CollapseProps {
  /** Whether the content is expanded */
  in?: boolean;
  /** Alias for 'in' (Material UI compatibility) */
  open?: boolean;
  /** Content to collapse/expand */
  children: React.ReactNode;
  /** Animation duration in ms */
  timeout?: number;
  /** Custom class name */
  className?: string;
  /** Callback when animation starts */
  onEnter?: () => void;
  /** Callback when animation ends */
  onEntered?: () => void;
  /** Callback when exit animation starts */
  onExit?: () => void;
  /** Callback when exit animation ends */
  onExited?: () => void;
  /** Orientation */
  orientation?: "vertical" | "horizontal";
  /** Collapsed size (for partial collapse) */
  collapsedSize?: number | string;
  /** Easing function */
  easing?: string;
  /** Unmount when collapsed */
  unmountOnExit?: boolean;
  /** Mount on first enter */
  mountOnEnter?: boolean;
}

/**
 * Arc-style Collapse component
 *
 * Features:
 * - Smooth height/width animation
 * - Vertical and horizontal orientation
 * - Customizable animation duration and easing
 * - Optional unmount on exit
 * - Partial collapse support
 */
const Collapse = forwardRef<HTMLDivElement, CollapseProps>(
  (
    {
      in: inProp,
      open,
      children,
      timeout = 300,
      className = "",
      onEnter,
      onEntered,
      onExit,
      onExited,
      orientation = "vertical",
      collapsedSize = 0,
      easing = "cubic-bezier(0.4, 0, 0.2, 1)",
      unmountOnExit = false,
      mountOnEnter = false,
    },
    ref
  ) => {
    // Support both 'in' and 'open' props (open is alias for Material UI compatibility)
    const isOpen = inProp ?? open ?? false;
    
    const contentRef = useRef<HTMLDivElement>(null);
    const [size, setSize] = useState<number | string>(isOpen ? "auto" : collapsedSize);
    const [isAnimating, setIsAnimating] = useState(false);
    const [shouldRender, setShouldRender] = useState(
      mountOnEnter ? isOpen : true
    );
    const [hasEntered, setHasEntered] = useState(isOpen);

    const isVertical = orientation === "vertical";
    const sizeProperty = isVertical ? "height" : "width";
    const scrollSizeProperty = isVertical ? "scrollHeight" : "scrollWidth";

    // Handle mount on enter
    useEffect(() => {
      if (isOpen && !shouldRender) {
        setShouldRender(true);
      }
    }, [isOpen, shouldRender]);

    // Handle animation
    useEffect(() => {
      if (!contentRef.current) return;

      const content = contentRef.current;
      const fullSize = content[scrollSizeProperty];

      if (isOpen) {
        // Opening
        onEnter?.();
        setIsAnimating(true);
        setHasEntered(true);

        // Start from collapsed size
        setSize(collapsedSize);

        // Force reflow
        content.offsetHeight;

        // Animate to full size
        requestAnimationFrame(() => {
          setSize(fullSize);
        });

        const timer = setTimeout(() => {
          setSize("auto");
          setIsAnimating(false);
          onEntered?.();
        }, timeout);

        return () => clearTimeout(timer);
      } else {
        // Closing
        onExit?.();
        setIsAnimating(true);

        // Set current size explicitly
        setSize(fullSize);

        // Force reflow
        content.offsetHeight;

        // Animate to collapsed size
        requestAnimationFrame(() => {
          setSize(collapsedSize);
        });

        const timer = setTimeout(() => {
          setIsAnimating(false);
          onExited?.();
          if (unmountOnExit) {
            setShouldRender(false);
          }
        }, timeout);

        return () => clearTimeout(timer);
      }
    }, [
      isOpen,
      timeout,
      collapsedSize,
      scrollSizeProperty,
      unmountOnExit,
      onEnter,
      onEntered,
      onExit,
      onExited,
    ]);

    // Don't render if unmountOnExit and not open
    if (!shouldRender) return null;

    // Don't render if mountOnEnter and never entered
    if (mountOnEnter && !hasEntered) return null;

    const style: React.CSSProperties = {
      [sizeProperty]: size,
      overflow: isAnimating || !isOpen ? "hidden" : "visible",
      transition: isAnimating
        ? `${sizeProperty} ${timeout}ms ${easing}`
        : undefined,
    };

    return (
      <div
        ref={(node) => {
          contentRef.current = node;
          if (typeof ref === "function") {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
        }}
        className={className}
        style={style}
      >
        {children}
      </div>
    );
  }
);

Collapse.displayName = "Collapse";

// Accordion components for convenience
export interface AccordionProps {
  /** Accordion items */
  children: React.ReactNode;
  /** Allow multiple panels open */
  multiple?: boolean;
  /** Default expanded panels (by index or id) */
  defaultExpanded?: (number | string)[];
  /** Controlled expanded panels */
  expanded?: (number | string)[];
  /** Callback when expanded changes */
  onExpandedChange?: (expanded: (number | string)[]) => void;
  /** Custom class name */
  className?: string;
}

export interface AccordionItemProps {
  /** Unique identifier */
  id?: string;
  /** Header content */
  header: React.ReactNode;
  /** Panel content */
  children: React.ReactNode;
  /** Disabled state */
  disabled?: boolean;
  /** Custom class name */
  className?: string;
  /** Custom header class name */
  headerClassName?: string;
  /** Custom content class name */
  contentClassName?: string;
  /** Icon position */
  iconPosition?: "left" | "right";
  /** Custom expand icon */
  expandIcon?: React.ReactNode;
}

// Accordion Context
interface AccordionContextType {
  expanded: (number | string)[];
  toggleExpanded: (id: number | string) => void;
}

const AccordionContext = React.createContext<AccordionContextType | null>(null);

/**
 * Accordion component (wrapper for multiple collapsible panels)
 */
const Accordion: React.FC<AccordionProps> = ({
  children,
  multiple = false,
  defaultExpanded = [],
  expanded: controlledExpanded,
  onExpandedChange,
  className = "",
}) => {
  const [internalExpanded, setInternalExpanded] =
    useState<(number | string)[]>(defaultExpanded);

  const isControlled = controlledExpanded !== undefined;
  const expanded = isControlled ? controlledExpanded : internalExpanded;

  const toggleExpanded = (id: number | string) => {
    const newExpanded = expanded.includes(id)
      ? expanded.filter((e) => e !== id)
      : multiple
        ? [...expanded, id]
        : [id];

    if (!isControlled) {
      setInternalExpanded(newExpanded);
    }
    onExpandedChange?.(newExpanded);
  };

  return (
    <AccordionContext.Provider value={{ expanded, toggleExpanded }}>
      <div className={`divide-y divide-arc-border ${className}`}>
        {React.Children.map(children, (child, index) => {
          if (React.isValidElement<AccordionItemProps>(child)) {
            return React.cloneElement(child, {
              id: child.props.id ?? index.toString(),
            });
          }
          return child;
        })}
      </div>
    </AccordionContext.Provider>
  );
};

/**
 * Accordion Item component
 */
const AccordionItem: React.FC<AccordionItemProps> = ({
  id = 0,
  header,
  children,
  disabled = false,
  className = "",
  headerClassName = "",
  contentClassName = "",
  iconPosition = "right",
  expandIcon,
}) => {
  const context = React.useContext(AccordionContext);
  const isExpanded = context?.expanded.includes(id) ?? false;

  const handleToggle = () => {
    if (disabled) return;
    context?.toggleExpanded(id);
  };

  const defaultIcon = (
    <svg
      className={`w-4 h-4 transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 9l-7 7-7-7"
      />
    </svg>
  );

  return (
    <div className={className}>
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={`
          w-full flex items-center gap-3
          py-3 px-4
          text-left
          transition-colors duration-arc-fast
          ${disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-surface-secondary cursor-pointer"}
          ${headerClassName}
        `}
        aria-expanded={isExpanded}
      >
        {iconPosition === "left" && (
          <span className="flex-shrink-0 text-content-tertiary">
            {expandIcon || defaultIcon}
          </span>
        )}
        <span className="flex-1 font-medium text-content-primary">{header}</span>
        {iconPosition === "right" && (
          <span className="flex-shrink-0 text-content-tertiary">
            {expandIcon || defaultIcon}
          </span>
        )}
      </button>
      <Collapse in={isExpanded}>
        <div className={`px-4 pb-4 ${contentClassName}`}>{children}</div>
      </Collapse>
    </div>
  );
};

export { Collapse, Accordion, AccordionItem };
export default Collapse;









